'use client';

import React, { createContext, ReactNode, useContext } from 'react';

import { Listing } from '@/graphql/types/listingInput';
import { useChat } from '@/providers/ChatContext';
import {
  ChannelsContextProps,
  Chat,
  ChatType,
  TemporaryChat
} from '@/types/chat';
import { User } from '@/types/user';
import {
  addChannelsToBottom,
  addChannelToBottom,
  addChannelToTop,
  moveChannelToTop,
  replaceChannel
} from '@/utils/helpers/chat';

const ChannelsContext = createContext<ChannelsContextProps | undefined>(
  undefined
);

export const ChannelsProvider = ({ children }: { children: ReactNode }) => {
  const { channels, setChannels, setIsChatsLoaded } = useChat();

  const getChannel = (chatId: string) => {
    return channels.find(c => c.id === chatId) || null;
  };

  const getChannelWithCreation = (
    chatId: string,
    channelProperties: { listing: Listing; receiver: User }
  ) => {
    const existingChannel = channels.find(c => c.id === chatId);

    if (existingChannel) return existingChannel;

    const newChannel: TemporaryChat = {
      id: chatId,
      createdAt: new Date().toISOString(),
      unreadCount: 0,
      ...channelProperties
    };

    setChannels(prev => [newChannel, ...prev]);

    return newChannel;
  };

  const addChannel = (newChannel: Chat) => {
    setChannels(prev => {
      return addChannelToBottom(prev, newChannel);
    });
  };

  const addChannels = (newChannels: Chat[]) => {
    setChannels(prev => {
      return addChannelsToBottom(prev, newChannels);
    });

    setIsChatsLoaded(true);
  };

  const upsertChannel = (newChannel: Chat) => {
    setChannels(prev => {
      return addChannelToTop(prev, newChannel);
    });
  };

  const removeChannel = (chatId: string) => {
    return setChannels(prev => prev.filter(channel => channel.id !== chatId));
  };

  const updateChannelById = (
    chatId: string,
    updater: (channel: ChatType) => ChatType
  ) => {
    setChannels(prev =>
      prev.map(channel => (channel.id === chatId ? updater(channel) : channel))
    );
  };

  const replaceChannelById = (storedId: string, newChannel: ChatType) => {
    setChannels(prev => replaceChannel(prev, storedId, newChannel));
  };

  const putChannelUp = (chatId: string) => {
    setChannels(prev => {
      return moveChannelToTop(prev, chatId);
    });
  };

  return (
    <ChannelsContext.Provider
      value={{
        getChannel,
        getChannelWithCreation,
        addChannel,
        upsertChannel,
        replaceChannelById,
        removeChannel,
        putChannelUp,
        addChannels,
        updateChannelById
      }}
    >
      {children}
    </ChannelsContext.Provider>
  );
};

export const useChannels = () => {
  const context = useContext(ChannelsContext);

  if (!context) {
    throw new Error('useChannels must be used within a ChannelsProvider');
  }

  return context;
};
